/**
 * Localized chrome of the modular What's New dialog (6 app locales: en, hu, de, cn, ro, svn).
 * The entries themselves carry their own `title` / `body` maps; this file only holds the dialog
 * labels and the display names of the modules that have entries.
 */

/** Dialog strings per locale. `{{module}}` is replaced with the module's display name. */
export const DIALOG_STRINGS = {
  en: {
    kicker: "What's new here",
    title: 'New in {{module}}',
    subtitle: 'Recent updates to this screen — shown only once.',
    got_it: 'Got it',
    see_all: 'All updates',
    close: 'Close',
  },
  hu: {
    kicker: 'Újdonság ezen a képernyőn',
    title: 'Újdonságok: {{module}}',
    subtitle: 'Friss fejlesztések ezen a képernyőn – csak egyszer jelennek meg.',
    got_it: 'Értem',
    see_all: 'Minden újdonság',
    close: 'Bezárás',
  },
  de: {
    kicker: 'Neu auf diesem Bildschirm',
    title: 'Neu bei {{module}}',
    subtitle: 'Aktuelle Neuerungen auf diesem Bildschirm – sie werden nur einmal angezeigt.',
    got_it: 'Verstanden',
    see_all: 'Alle Neuerungen',
    close: 'Schließen',
  },
  cn: {
    kicker: '本页新功能',
    title: '{{module}}新功能',
    subtitle: '本页的最新更新——仅显示一次。',
    got_it: '知道了',
    see_all: '全部更新',
    close: '关闭',
  },
  ro: {
    kicker: 'Noutăți pe acest ecran',
    title: 'Noutăți în {{module}}',
    subtitle: 'Actualizări recente ale acestui ecran – apar o singură dată.',
    got_it: 'Am înțeles',
    see_all: 'Toate noutățile',
    close: 'Închide',
  },
  svn: {
    kicker: 'Novo na tem zaslonu',
    title: 'Novo v {{module}}',
    subtitle: 'Nedavne posodobitve tega zaslona – prikazane samo enkrat.',
    got_it: 'Razumem',
    see_all: 'Vse posodobitve',
    close: 'Zapri',
  },
};

/** Display name of each module that has entries (add one line per new module). */
export const MODULE_LABELS = {
  driver: {
    en: 'Driver',
    hu: 'Sofőr',
    de: 'Fahrer',
    cn: '司机',
    ro: 'Șofer',
    svn: 'Voznik',
  },
  novoPayment: {
    en: 'NovoPayment',
    hu: 'NovoPayment',
    de: 'NovoPayment',
    cn: 'NovoPayment',
    ro: 'NovoPayment',
    svn: 'NovoPayment',
  },
  linkedin: {
    en: 'LinkedIn',
    hu: 'LinkedIn',
    de: 'LinkedIn',
    cn: 'LinkedIn',
    ro: 'LinkedIn',
    svn: 'LinkedIn',
  },
  bobo: {
    en: 'Bobo',
    hu: 'Bobo',
    de: 'Bobo',
    cn: 'Bobo',
    ro: 'Bobo',
    svn: 'Bobo',
  },
  happyAi: {
    en: 'Happy AI',
    hu: 'Happy AI',
    de: 'Happy AI',
    cn: 'Happy AI',
    ro: 'Happy AI',
    svn: 'Happy AI',
  },
  aiAgent: {
    en: 'AI agent',
    hu: 'AI ügynök',
    de: 'KI-Agent',
    cn: 'AI 智能体',
    ro: 'Agent AI',
    svn: 'AI agent',
  },
  leads: {
    en: 'Leads',
    hu: 'Leadek',
    de: 'Leads',
    cn: '潜在客户',
    ro: 'Potențiali clienți',
    svn: 'Potencialni kupci',
  },
  delegation: {
    en: 'Delegation',
    hu: 'Delegálás',
    de: 'Delegierung',
    cn: '委派',
    ro: 'Delegare',
    svn: 'Delegiranje',
  },
  mail: {
    en: 'Mail',
    hu: 'Levelezés',
    de: 'E-Mail',
    cn: '邮件',
    ro: 'E-mail',
    svn: 'E-pošta',
  },
  myDay: {
    en: 'My Day',
    hu: 'Mai napom',
    de: 'Mein Tag',
    cn: '我的一天',
    ro: 'Ziua mea',
    svn: 'Moj dan',
  },
  statistics: {
    en: 'Statistics',
    hu: 'Statisztika',
    de: 'Statistik',
    cn: '统计',
    ro: 'Statistici',
    svn: 'Statistika',
  },
  teamChat: {
    en: 'Team Chat',
    hu: 'Csapatchat',
    de: 'Team-Chat',
    cn: '团队聊天',
    ro: 'Chat echipă',
    svn: 'Skupinski klepet',
  },
  utilities: {
    en: 'Utilities',
    hu: 'Rezsi',
    de: 'Nebenkosten',
    cn: '水电费',
    ro: 'Utilități',
    svn: 'Komunalni stroški',
  },
  payroll: {
    en: 'Payroll',
    hu: 'Bérszámfejtés',
    de: 'Lohnabrechnung',
    cn: '工资核算',
    ro: 'Salarizare',
    svn: 'Obračun plač',
  },
  timeTracking: {
    en: 'Time tracking',
    hu: 'Időmérés',
    de: 'Zeiterfassung',
    cn: '时间记录',
    ro: 'Pontaj',
    svn: 'Merjenje časa',
  },
  widgets: {
    en: 'Widgets',
    hu: 'Widgetek',
    de: 'Widgets',
    cn: '小组件',
    ro: 'Widgeturi',
    svn: 'Pripomočki',
  },
  businessAi: {
    en: 'Business insights',
    hu: 'Üzleti összefoglaló',
    de: 'Business-Digest',
    cn: '业务洞察',
    ro: 'Informații de business',
    svn: 'Poslovni vpogledi',
  },
  intelligence: {
    en: 'Intelligence',
    hu: 'Intelligencia',
    de: 'Intelligenz',
    cn: '智能分析',
    ro: 'Inteligență',
    svn: 'Inteligenca',
  },
  smartFilter: {
    en: 'Smart filter',
    hu: 'Okos szűrő',
    de: 'Smart-Filter',
    cn: '智能筛选',
    ro: 'Filtru inteligent',
    svn: 'Pametni filter',
  },
  aiAssistant: {
    en: 'AI assistant',
    hu: 'AI asszisztens',
    de: 'KI-Assistent',
    cn: 'AI 助手',
    ro: 'Asistent AI',
    svn: 'AI pomočnik',
  },
  voip: {
    en: 'Phone calls',
    hu: 'Telefonálás',
    de: 'Telefonie',
    cn: '电话通话',
    ro: 'Apeluri telefonice',
    svn: 'Klici',
  },
  service: {
    en: 'Service',
    hu: 'Szerviz',
    de: 'Service',
    cn: '服务',
    ro: 'Service',
    svn: 'Servis',
  },
};

/**
 * Humanize a module key for the fallback label ("work-orders" => "Work orders").
 * @param {string} moduleKey Module key.
 * @returns {string}
 */
export const humanizeModule = (moduleKey) => {
  const text = String(moduleKey || '').replace(/[-_]+/g, ' ').trim();

  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
};

/**
 * Resolve the dialog strings for a language, falling back to English.
 * @param {string} language Active i18n language (e.g. "hu", "sl-SI" is not used: the app uses "svn").
 * @returns {Object}
 */
export const getDialogStrings = (language) => DIALOG_STRINGS[String(language || 'en').split('-')[0].toLowerCase()] || DIALOG_STRINGS.en;

/**
 * Resolve a module's display name for a language.
 * @param {string} moduleKey Module key.
 * @param {string} language Active i18n language.
 * @returns {string}
 */
export const getModuleLabel = (moduleKey, language) => {
  const base = String(language || 'en').split('-')[0].toLowerCase();
  const labels = MODULE_LABELS[moduleKey];

  return (labels && (labels[base] || labels.en)) || humanizeModule(moduleKey);
};
